import { useEffect, useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { useLang, api } from "../store";
import { PageHead, Reveal } from "../components/Reveal";
import { RequestForm } from "../components/RequestForm";
import Footer from "../components/Footer";

const PALMA_URL = "https://www.thepalmamovie.com";

export default function Productions() {
  const { lang, t } = useLang();
  const [items, setItems] = useState([]);

  useEffect(() => {
    api.get("/productions").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);

  return (
    <div className="min-h-screen" data-testid="productions-page">
      <PageHead kicker="THE PALMA MOVIE · PRODUZIONE" title={t("productions.title")} sub={t("productions.sub")} />

      <div className="mx-auto max-w-[1600px] px-6 pb-24">
        <div className="grid gap-10 lg:grid-cols-[1fr_1.1fr]">
          <Reveal>
            <p className="font-serif-it text-xl italic leading-relaxed text-slate-300 sm:text-2xl" data-testid="palma-intro">
              {t("productions.partner")}
            </p>
            <a
              href={PALMA_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="palma-link"
              className="font-display mt-7 inline-flex items-center gap-2 rounded bg-amber-600 px-7 py-3 text-sm uppercase tracking-[0.2em] text-black transition-colors hover:bg-amber-500"
            >
              {t("productions.visit")} <ArrowUpRight size={16} />
            </a>
          </Reveal>

          <Reveal delay={0.12}>
            <a
              href={PALMA_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="palma-preview"
              className="group block overflow-hidden rounded border border-white/10 bg-[#0e1014] transition-colors hover:border-amber-500/60"
            >
              <div className="flex items-center gap-2 border-b border-white/10 bg-[#14181f] px-4 py-2.5">
                <span className="h-2.5 w-2.5 rounded-full bg-red-500/70" />
                <span className="h-2.5 w-2.5 rounded-full bg-amber-500/70" />
                <span className="h-2.5 w-2.5 rounded-full bg-emerald-500/70" />
                <span className="font-mono2 ml-3 truncate text-[11px] tracking-widest text-slate-500">www.thepalmamovie.com</span>
              </div>
              <div className="relative aspect-[16/10] overflow-hidden">
                <img
                  src="/media/palmamovie-preview.jpg"
                  alt="The Palma Movie"
                  loading="lazy"
                  className="h-full w-full object-cover object-top transition-transform duration-[1200ms] group-hover:scale-[1.03]"
                />
                <span className="font-mono2 absolute bottom-4 right-4 flex items-center gap-2 rounded-full bg-black/80 px-4 py-2 text-[11px] tracking-widest text-amber-400 opacity-0 backdrop-blur transition-opacity group-hover:opacity-100">
                  {t("productions.visit")} <ArrowUpRight size={13} />
                </span>
              </div>
            </a>
          </Reveal>
        </div>

        <Reveal className="mt-16">
          <div className="rounded border border-amber-600/30 bg-[#141009] p-8">
            <p className="text-base leading-relaxed text-slate-300 sm:text-lg" data-testid="productions-pitch">{t("productions.pitch")}</p>
          </div>
        </Reveal>

        <div className="mt-14 max-w-3xl">
          <Reveal>
            <RequestForm subject="Produzione — valutazione progetto" testid="production-request-form" />
          </Reveal>
        </div>

        {items.length > 0 && (
          <>
            <Reveal className="mt-20">
              <h2 className="font-display text-2xl uppercase tracking-tight sm:text-3xl">{t("productions.titles")}</h2>
              <p className="mt-2 text-sm text-slate-500">{t("productions.titlesSub")}</p>
            </Reveal>
            <div className="mt-8 grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-4" data-testid="productions-grid">
              {items.map((p, i) => (
                <Reveal key={p.id} delay={(i % 4) * 0.07}>
                  <a
                    href={p.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="dc-card group block h-full overflow-hidden rounded transition-colors hover:border-amber-500/50"
                    data-testid={`production-${p.id}`}
                  >
                    <div className="poster-frame relative aspect-[2/3] overflow-hidden bg-[#14181f]">
                      <img src={p.poster} alt={p.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-[900ms] group-hover:scale-[1.04]" />
                      <span className="font-mono2 absolute left-2 top-2 rounded bg-black/80 px-2 py-1 text-[9px] uppercase tracking-widest text-amber-500">
                        {p.kind} · {p.year}
                      </span>
                    </div>
                    <div className="p-4">
                      <h3 className="font-display text-base uppercase leading-tight tracking-wide">{p.title}</h3>
                      <p className="mt-2 text-xs leading-relaxed text-slate-400">{lang === "it" ? p.desc_it : p.desc_en}</p>
                      <span className="font-mono2 mt-3 inline-flex items-center gap-1.5 text-[10px] tracking-widest text-slate-500 transition-colors group-hover:text-amber-400">
                        {t("productions.openSheet")} <ArrowUpRight size={12} />
                      </span>
                    </div>
                  </a>
                </Reveal>
              ))}
            </div>
          </>
        )}
      </div>
      <Footer />
    </div>
  );
}
